import { createFractalWorker } from './createFractalWorker';
import { finalizeNebulabrotPixels, getNebulabrotSampleGrid } from './fractal_math';
import { isMobileViewport } from './fullscreenControl';

const MIN_PIXELS_FOR_WORKER = 80000;
const STRIPES_PER_WORKER = 3;
const IDLE_POOL_TIMEOUT_MS = 3000;
const clients = new Set();
const pendingRequests = new Map();
let workers = [];
let requestId = 0;
let nextWorker = 0;
let failed = false;
let idleTimer = null;

function clearIdleTimer() {
  if (idleTimer !== null) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

function scheduleIdleStop() {
  clearIdleTimer();
  if (pendingRequests.size > 0 || workers.length === 0) return;
  idleTimer = setTimeout(() => {
    idleTimer = null;
    if (pendingRequests.size === 0 && workers.length > 0) {
      stopWorkers(new Error('Idle workers parked'));
    }
  }, IDLE_POOL_TIMEOUT_MS);
  idleTimer?.unref?.();
}

function canUseWorkers() {
  return typeof Worker !== 'undefined' &&
    (typeof window === 'undefined' ||
      (window.location.protocol !== 'file:' && window.location.origin !== 'null'));
}

function stopWorkers(error) {
  clearIdleTimer();
  workers.forEach(worker => {
    worker.onmessage = null;
    worker.onerror = null;
    worker.onmessageerror = null;
    worker.terminate();
  });
  workers = [];
  nextWorker = 0;
  pendingRequests.forEach(request => request.reject(error));
  pendingRequests.clear();
}

function failPool(error) {
  failed = true;
  stopWorkers(error);
}

function ensureWorkers() {
  clearIdleTimer();
  if (failed || !canUseWorkers()) throw new Error('Workers unavailable');
  if (workers.length > 0) return;

  const mobile = isMobileViewport();
  const maxPoolSize = mobile ? 4 : 8;
  const poolSize = Math.min(navigator.hardwareConcurrency || 4, maxPoolSize);
  try {
    for (let i = 0; i < poolSize; i++) {
      const worker = createFractalWorker();
      workers.push(worker);
      worker.onmessage = ({ data: { id, type, data, error } }) => {
        const pending = pendingRequests.get(id);
        if (!pending || pending.worker !== worker) return;
        pendingRequests.delete(id);
        if (pendingRequests.size === 0) scheduleIdleStop();
        if (type === 'error') pending.reject(new Error(error));
        else {
          try {
            pending.resolve(pending.onResult ? pending.onResult(data) : data);
          } catch (resultError) {
            pending.reject(resultError);
          }
        }
      };
      // A crashed worker must reject outstanding work so callers can fall back.
      worker.onerror = event => failPool(new Error(event.message || 'Fractal worker failed'));
      worker.onmessageerror = () => failPool(new Error('Fractal worker response could not be read'));
    }
  } catch (error) {
    // Includes workers created before a later constructor failed.
    failPool(error);
    throw error;
  }
}

function postMessage(client, worker, type, params, onResult) {
  return new Promise((resolve, reject) => {
    const id = ++requestId;
    pendingRequests.set(id, { client, worker, resolve, reject, onResult });
    try {
      worker.postMessage({ type, id, params });
    } catch (error) {
      pendingRequests.delete(id);
      if (pendingRequests.size === 0) scheduleIdleStop();
      reject(error);
    }
  });
}

// Local scheduling control stays outside serializable Worker parameters.
function assertRenderCurrent(control) {
  if (control?.shouldContinue && !control.shouldContinue()) {
    const error = new Error('Fractal render superseded');
    error.name = 'AbortError';
    throw error;
  }
}

/** Register a canvas without starting workers. The first CPU render starts the shared pool. */
export function createFractalWorkerClient() {
  const client = {};
  clients.add(client);

  function prepareRender() {
    if (!clients.has(client)) throw new Error('Worker client released');
    ensureWorkers();
  }

  return {
    isWorkerAvailable() {
      return clients.has(client) && !failed && canUseWorkers();
    },

    shouldUseWorker(width, height) {
      return width * height >= MIN_PIXELS_FOR_WORKER &&
        clients.has(client) && !failed && canUseWorkers();
    },

    async renderFull(params, control) {
      assertRenderCurrent(control);
      prepareRender();
      const { canvasWidth, canvasHeight } = params;
      const renderWorkers = [...workers];
      if (params.colorParams?.mode === 'nebulabrot') {
        const { samplesX, samplesY } = getNebulabrotSampleGrid(canvasWidth, canvasHeight);
        const pixelCount = canvasWidth * canvasHeight;
        // When other canvases already have requests in flight (e.g. scrolling
        // into multiple Nebulabrot views), dispatch the full Nebulabrot job to
        // one round-robin worker so accumulation and tone-mapping both run
        // off the main thread without transferring 4-plane float histograms.
        if (pendingRequests.size > 0 || renderWorkers.length <= 1 || samplesY <= 1) {
          const worker = renderWorkers[nextWorker];
          nextWorker = (nextWorker + 1) % renderWorkers.length;
          const expectedBytes = pixelCount * 4;
          return postMessage(client, worker, 'renderFull', {
            ...params,
            samplesX,
            samplesY,
          }, rgba => {
            assertRenderCurrent(control);
            if (!(rgba instanceof Uint8ClampedArray) || rgba.length !== expectedBytes) {
              throw new Error('Invalid worker render stripe');
            }
            return rgba;
          });
        }
        const maxSlices = pendingRequests.size >= renderWorkers.length ? 2 : 4;
        const sliceCount = Math.max(1, Math.min(maxSlices, renderWorkers.length, samplesY));
        const startWorker = nextWorker;
        nextWorker = (nextWorker + sliceCount) % renderWorkers.length;
        const selectedWorkers = Array.from(
          { length: sliceCount },
          (_, i) => renderWorkers[(startWorker + i) % renderWorkers.length],
        );
        const rowsPerWorker = Math.ceil(samplesY / sliceCount);
        const expectedLen = pixelCount * 4;
        let combinedHist = null;
        let nextSampleY = 0;
        let stopped = false;
        await Promise.all(selectedWorkers.map(async worker => {
          while (nextSampleY < samplesY && !stopped) {
            if (!clients.has(client)) throw new Error('Worker client released');
            try {
              assertRenderCurrent(control);
              const startSampleY = nextSampleY;
              nextSampleY += rowsPerWorker;
              const sampleRegionHeight = Math.min(rowsPerWorker, samplesY - startSampleY);
              await postMessage(client, worker, 'renderNebulabrotSlice', {
                ...params,
                samplesX,
                samplesY,
                startSampleY,
                sampleRegionHeight,
              }, sliceHist => {
                if (!(sliceHist instanceof Float32Array) || sliceHist.length !== expectedLen) {
                  throw new Error('Invalid worker nebulabrot histogram');
                }
                if (!combinedHist) {
                  combinedHist = sliceHist;
                } else {
                  for (let i = 0; i < expectedLen; i++) {
                    combinedHist[i] += sliceHist[i];
                  }
                }
              });
            } catch (error) {
              stopped = true;
              throw error;
            }
          }
        }));
        assertRenderCurrent(control);
        return finalizeNebulabrotPixels(
          combinedHist || new Float32Array(expectedLen),
          canvasWidth,
          canvasHeight,
        );
      }
      const rowsPerStripe = Math.ceil(canvasHeight
        / (renderWorkers.length * STRIPES_PER_WORKER));
      const pixels = new Uint8ClampedArray(canvasWidth * canvasHeight * 4);
      let nextStartY = 0;
      let stopped = false;
      await Promise.all(renderWorkers.map(async worker => {
        while (nextStartY < canvasHeight && !stopped) {
          if (!clients.has(client)) throw new Error('Worker client released');
          try {
            assertRenderCurrent(control);
            const startY = nextStartY;
            nextStartY += rowsPerStripe;
            const regionHeight = Math.min(rowsPerStripe, canvasHeight - startY);
            const stripeBytes = canvasWidth * regionHeight * 4;
            await postMessage(client, worker, 'renderFull', {
              ...params,
              startY,
              regionHeight,
            }, stripe => {
              if (!(stripe instanceof Uint8ClampedArray) || stripe.length !== stripeBytes) {
                throw new Error('Invalid worker render stripe');
              }
              pixels.set(stripe, startY * canvasWidth * 4);
            });
          } catch (error) {
            stopped = true;
            throw error;
          }
        }
      }));
      assertRenderCurrent(control);
      return pixels;
    },

    async renderPan(params) {
      prepareRender();
      const worker = workers[nextWorker];
      nextWorker = (nextWorker + 1) % workers.length;
      return postMessage(client, worker, 'renderPan', params);
    },

    release() {
      if (!clients.delete(client)) return;
      // Other canvases keep their workers and in-flight requests.
      for (const [id, request] of pendingRequests) {
        if (request.client === client) {
          pendingRequests.delete(id);
          request.reject(new Error('Worker client released'));
        }
      }
      if (clients.size === 0) {
        stopWorkers(new Error('Workers terminated'));
        failed = false;
      } else if (pendingRequests.size === 0) {
        scheduleIdleStop();
      }
    },
  };
}
